import {
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  FormHelperText,
  Grid,
  TextField
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import MuiAccordion from '@mui/material/Accordion';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import AutocompleteInput from 'components/Autocomplete/AutocompleteInput';
import Button from 'tailwind-components/Button/Button';
import { STATE_LIST, USER_TYPES } from 'constants/enum';
import icons from 'constants/icons';
import PropTypes from 'prop-types';
import React, { memo, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAppointmentsViewData,
  getSelectedAgenciesAgents,
  getSpecificAgencyData,
  getStatementsPeriods
} from 'state/actions/actions';
import style from '../DashboardCommissionReco.module.scss';
import SimpleFilter from './SimpleFilter/SimpleFilter';

const Accordion = styled(props => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0
  },
  '&::before': {
    display: 'none'
  },
  color: '#656464'
}));

const FilterIcon = icons['filterList'];
const ExpandMoreIcon = icons['arrowDropDown'];

const CommissionRecoTableFilters = ({
  localLoader,
  filterCount,
  formData,
  clearFilters,
  handleChangeSelectValue,
  applyFilters,
  setformData,
  showFilters
}) => {
  const dispatch = useDispatch();
  const cancelTokenSource = axios.CancelToken.source();

  // Local states
  const [, setAgentsLoaded] = useState(false);
  const [allLoaded, setAllLoaded] = useState(false);
  const initialLoad = useRef(true);

  const { satementsCarriersArray } = useSelector(state => state.appointments);

  const { zohoData } = useSelector(state => state.user);

  const currentAgenciesAgentsDict = useSelector(
    state => state.agencyManagement.comissions_tab_agents
  );

  const { agencyInformation } = useSelector(
    state => state.agencyManagement.agencyData
  );

  const carriersOptions = React.useMemo(() => {
    return satementsCarriersArray
      .map(option => {
        return {
          type: option.type,
          name: option.name,
          id: option.AgencyBloc_CarrierID,
          Callidus_Carrier_Names: option.Callidus_Carrier_Names
        };
      })
      .concat([
        {
          type: 'Marketplace',
          name: 'Other',
          id: '',
          Callidus_Carrier_Names: 'Other'
        }
      ])
      .filter(carrier => carrier.type);
  }, [satementsCarriersArray]);

  const multiSelectOptions = React.useMemo(() => {
    if (zohoData.Position_at_Agency !== USER_TYPES.ADMIN) return [];

    const filteredAgenciesOptions =
      agencyInformation.Downline_Agencies_Three?.concat([agencyInformation]);

    const returnAgenciesOptions =
      filteredAgenciesOptions?.map(agency => ({
        id: agency.id._value,
        name: agency.name._value
      })) || [];

    // Gets all the agents from all the agencies from the tree
    if (zohoData.Position_at_Agency === USER_TYPES.ADMIN) {
      const ids = [...returnAgenciesOptions?.map(agency => agency.id)];
      dispatch(
        getSelectedAgenciesAgents(
          () => {},
          currentAgenciesAgentsDict,
          ids,
          cancelTokenSource
        )
      );
    }

    return returnAgenciesOptions;
  }, [agencyInformation.Downline_Agencies_Three]);

  const agentsOptions = React.useMemo(() => {
    if (!zohoData.NPN || multiSelectOptions.length === 0) return [];

    const selectedAgenciesAgents = multiSelectOptions
      .map(agency =>
        (currentAgenciesAgentsDict[agency.id] ?? []).map(agent => ({
          type: agent.Position_at_Agency,
          name: agent.First_Name + ' ' + agent.Last_Name,
          npn: agent.NPN,
          agentId: agent.id,
          agency: agency.name,
          agencyId: agency.id,
          Position_at_Agency: agent.Position_at_Agency
        }))
      )
      .flat();

    setAgentsLoaded(true);

    return selectedAgenciesAgents;
  }, [currentAgenciesAgentsDict, multiSelectOptions]);

  // Effects
  // Gets data from backend
  useEffect(() => {
    const getData = async () => {
      dispatch(
        getStatementsPeriods(
          () => {},
          () => {}
        )
      );
      dispatch(
        getAppointmentsViewData(
          () => {},
          () => {}
        )
      );
      if (zohoData.Position_at_Agency !== USER_TYPES.ADMIN) return;
      dispatch(
        getSpecificAgencyData(
          () => {},
          zohoData?.Account_Name?.id,
          () => {}
        )
      );
    };
    getData();
  }, []);

  // If all data from all sources has finished loading, turns off the local loader
  useEffect(() => {
    const carriersOptionsAreLoaded = carriersOptions.length > 1;
    const agenciesOptionsAreLoaded =
      multiSelectOptions.length > 0 ||
      zohoData.Position_at_Agency !== USER_TYPES.ADMIN;
    const agentsOptionsAreLoaded = agentsOptions.length > 0;

    if (
      carriersOptionsAreLoaded &&
      agentsOptionsAreLoaded &&
      agenciesOptionsAreLoaded &&
      !allLoaded
    ) {
      setAllLoaded(true);
    }
  }, [carriersOptions, agentsOptions, multiSelectOptions]);

  // Preselects main agent
  useEffect(() => {
    if (zohoData.NPN && allLoaded) {
      setformData(prevData => ({
        ...prevData,
        agents:
          zohoData.Position_at_Agency === 'Admin'
            ? agentsOptions
            : [
                {
                  type: 'Main Agent',
                  name: zohoData.First_Name + ' ' + zohoData.Last_Name,
                  npn: zohoData.NPN,
                  agentId: zohoData?.id,
                  agency: zohoData.agencyData.Account_Name,
                  agencyId: zohoData.Account_Name?.id,
                  Position_at_Agency: zohoData.Position_at_Agency
                }
              ]
      }));
    }
  }, [agentsOptions, allLoaded, zohoData]);

  useEffect(() => {
    if (allLoaded && formData.agents.length > 0 && initialLoad.current) {
      applyFilters();
      initialLoad.current = false;
    }
  }, [formData.agents]);

  return (
    <div
      className={style.filtersContainer}
      style={{
        display: showFilters ? 'block' : 'none',
        overflowY: 'auto'
      }}
    >
      <div
        className={style.filtersHeader}
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}
      >
        <FilterIcon />
        <h4>Filters ({filterCount})</h4>
      </div>
      <div className={style.filters}>
        <div
          style={{
            borderBottom: '1px solid #f6f6f6',
            width: '85%'
          }}
        />
        <Grid
          container
          columnSpacing={{ xs: 0, sm: 2, lg: 0 }}
          rowSpacing={{ xs: 2, sm: 2, md: 3 }}
          style={{
            width: '85%',
            marginTop: '20px',
            margin: '20px 0px'
          }}
        >
          {zohoData.Position_at_Agency === USER_TYPES.ADMIN && (
            <>
              <Grid item xs={12} sm={6} lg={12}>
                <AutocompleteInput
                  label={'agents'}
                  multiple
                  selectAll
                  loading={!allLoaded}
                  disabled={!allLoaded}
                  options={!allLoaded ? [] : agentsOptions}
                  isOptionEqualToValue={(option, value) =>
                    option.name === value.name
                  }
                  groupBy={option => option.agency}
                  getOptionLabel={option => option.name}
                  id='downlineagents'
                  value={!allLoaded ? [] : formData.agents}
                  onValueChanges={e => handleChangeSelectValue(e, 'agents')}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={'Agent(s)'}
                      placeholder={'Select agents'}
                      InputLabelProps={{
                        shrink: true
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {!allLoaded ? (
                              <CircularProgress color='inherit' size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        )
                      }}
                    />
                  )}
                  selectAllByGroup={true}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12} sm={6} lg={12}>
            <Accordion slotProps={{ heading: { component: 'h4' } }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls='carrier-header'
                id='carrier-header'
              >
                Carrier
              </AccordionSummary>
              <AccordionDetails
                style={{
                  borderLeft: '5px solid #d0d0d1'
                }}
              >
                <AutocompleteInput
                  multiple
                  loading={localLoader}
                  options={carriersOptions
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .sort((a, b) => -b.type.localeCompare(a.type))}
                  isOptionEqualToValue={(option, value) =>
                    option.name === value.name
                  }
                  groupBy={option => option.type}
                  getOptionLabel={option => option.name}
                  id='carriers'
                  value={formData.carriers}
                  onValueChanges={e => handleChangeSelectValue(e, 'carriers')}
                  disabled={formData.includeAllCarriers}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label='Carrier(s)'
                      placeholder={'Select carriers'}
                      InputLabelProps={{
                        shrink: !formData.includeAllCarriers
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {localLoader ? (
                              <CircularProgress color='inherit' size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        )
                      }}
                    />
                  )}
                />
              </AccordionDetails>
            </Accordion>
          </Grid>
          <Grid item xs={12} sm={6} lg={12}>
            <Accordion slotProps={{ heading: { component: 'h4' } }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls='pstate-header'
                id='state-header'
              >
                State
              </AccordionSummary>
              <AccordionDetails
                style={{
                  borderLeft: '5px solid #d0d0d1'
                }}
              >
                <SimpleFilter
                  options={STATE_LIST}
                  value={formData.states}
                  handleChange={event => {
                    const {
                      target: { value }
                    } = event;

                    handleChangeSelectValue(value, 'states');
                  }}
                />
              </AccordionDetails>
            </Accordion>
          </Grid>
          <Grid item xs={12} sm={6} lg={12}>
            <Accordion slotProps={{ heading: { component: 'h4' } }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls='dates-header'
                id='dates-header'
              >
                Initial Date
              </AccordionSummary>
              <AccordionDetails
                style={{
                  borderLeft: '5px solid #d0d0d1',
                  paddingBottom: '20px'
                }}
              >
                <Box
                  flexGrow={1}
                  display='flex'
                  flexDirection='column'
                  alignItems='center'
                  width='100%'
                  sx={{ mt: 2 }}
                >
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label='Initial Date'
                      value={
                        formData.initial_date && formData.initial_date[0]
                          ? dayjs(formData.initial_date[0], 'MM-YYYY')
                          : dayjs().subtract(1, 'year')
                      }
                      onChange={date => {
                        const formattedDate = date
                          ? date.format('MM-YYYY')
                          : '';
                        handleChangeSelectValue(formattedDate, 'initial_date');
                      }}
                      views={['year', 'month']}
                      openTo='month'
                      minDate={dayjs().subtract(10, 'year')}
                      maxDate={dayjs().subtract(1, 'month')}
                      renderInput={params => (
                        <TextField {...params} fullWidth />
                      )}
                      slotProps={{
                        textField: {
                          fullWidth: true
                        }
                      }}
                    />
                  </LocalizationProvider>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Grid>
          <Grid item xs={12} sm={6} lg={12}>
            <Accordion slotProps={{ heading: { component: 'h4' } }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls='agent-status-header'
                id='agent-status-header'
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <span>Client Status</span>
                </div>
              </AccordionSummary>
              <AccordionDetails>
                <SimpleFilter
                  options={['Active', 'Inactive']}
                  value={formData.agentStatus}
                  handleChange={event => {
                    const {
                      target: { value }
                    } = event;

                    handleChangeSelectValue(value, 'agentStatus');
                  }}
                />
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
        <div
          className={style.clearApplyFilters}
          style={{ flexDirection: 'column' }}
        >
          {Array.isArray(formData.agentStatus) &&
            formData.agentStatus.length === 0 && (
              <FormHelperText
                error
                sx={{
                  mb: 0.5,
                  fontSize: '0.75rem',
                  width: '100%',
                  marginLeft: '30px'
                }}
              >
                A Client status must be selected
              </FormHelperText>
            )}
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '12px',
              justifyContent: 'center'
            }}
          >
            <Button
              text='Clear All'
              size='medium'
              variant='secondary'
              onClick={() => clearFilters()}
            />
            <Button
              text='Apply Filters'
              size='medium'
              variant='primaryDarkened'
              isDisabled={
                Array.isArray(formData.agentStatus) &&
                formData.agentStatus.length === 0
              }
              onClick={applyFilters}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

CommissionRecoTableFilters.propTypes = {
  localLoader: PropTypes.bool,
  filterCount: PropTypes.number,
  formData: PropTypes.object,
  clearFilters: PropTypes.func,
  handleChangeSelectValue: PropTypes.func,
  applyFilters: PropTypes.func,
  setformData: PropTypes.func,
  showFilters: PropTypes.bool
};

export default memo(CommissionRecoTableFilters);
